import {
  ScaffolderFormPaper,
  ScaffolderFormHeader,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React from 'react';

import { Box, FormControlLabel, Checkbox } from '@material-ui/core';

import { AUTO_SYNC_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { AutoSyncConfig, AutoSyncOptions } from '../types';

interface AutoSyncSectionProps {
  value: AutoSyncConfig;
  onChange: (value: AutoSyncConfig) => void;
}

const AutoSyncSectionInner = ({ value, onChange }: AutoSyncSectionProps) => {
  const classes = useStyles();

  const handleAutoSyncEnabledChange = (checked: boolean) => {
    onChange({
      enabled: checked,
      options: {
        prune: false,
        selfHeal: false,
        allowEmpty: false,
      },
    });
  };

  const handleAutoSyncOptionChange = (
    field: keyof AutoSyncOptions,
    checked: boolean,
  ) => {
    const updatedOptions: AutoSyncOptions = {
      ...value.options,
      [field]: checked,
    };
    onChange({
      enabled: value.enabled,
      options: updatedOptions,
    });
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Auto Sync Configuration"
          description="Customize the ArgoCD application's automated sync policy."
          divider
          infoDoc={AUTO_SYNC_INFO}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Enable Auto Sync"
            description="Enable or disable automatic synchronization for the ArgoCD application."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={value.enabled}
                onChange={e => handleAutoSyncEnabledChange(e.target.checked)}
                color="secondary"
              />
            }
            label="Enable Auto Sync"
          />
        </Box>
        <Box
          className={`${classes.inputContainer} ${classes.flexColumnContainer}`}
        >
          <ScaffolderInputHeader
            title="Auto Sync Options"
            description="Configure the options for automatic synchronization of the ArgoCD application."
          />
          <FormControlLabel
            disabled={!value.enabled}
            control={
              <Checkbox
                checked={value.options?.prune}
                onChange={e =>
                  handleAutoSyncOptionChange('prune', e.target.checked)
                }
                color="secondary"
              />
            }
            label="Prune - Delete resources that are no longer defined in Git"
          />
          <FormControlLabel
            disabled={!value.enabled}
            control={
              <Checkbox
                checked={value.options?.selfHeal}
                onChange={e =>
                  handleAutoSyncOptionChange('selfHeal', e.target.checked)
                }
                color="secondary"
              />
            }
            label="Self Heal - Automatically correct drift from desired state"
          />
          <FormControlLabel
            disabled={!value.enabled}
            control={
              <Checkbox
                checked={value.options?.allowEmpty}
                onChange={e =>
                  handleAutoSyncOptionChange('allowEmpty', e.target.checked)
                }
                color="secondary"
              />
            }
            label="Allow Empty - Permit sync when no resources are found"
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const AutoSyncSection = React.memo(AutoSyncSectionInner);
